import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { sendChatStream } from "./api";

/**
 * Single shared chat conversation between the AI Copilot Rail and the
 * full-screen Chat page. Whichever surface is mounted shows the same
 * messages and feeds the same session id to the backend.
 *
 * Optional `context` (passed to send) attaches a small chip to the user
 * message — e.g. "מק"ט 195 · קש פלסטיק" — so the implementer / auditor can
 * instantly see which row of which table the question came from.
 *
 * Streaming: send() now uses /api/chat/stream (SSE). Instead of waiting
 * 25-30 seconds for a single buffered response, the UI gets phase events
 * (router → tools → synth → done) and surfaces them as a status line.
 * This turns "frozen screen" into "AI is thinking out loud".
 *
 * State is persisted to sessionStorage so a browser refresh doesn't wipe
 * the conversation (matters for demos and QA testing).
 */
const ChatContext = createContext(null);
const STORAGE_KEY = "pulse_chat_state_v1";

function loadInitial() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.messages)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function ChatProvider({ children }) {
  const initial = loadInitial();
  const [messages, setMessages] = useState(initial?.messages || []);
  const [loading, setLoading] = useState(false);
  // The current phase label (e.g. "בודק מדדי מלאי…"). null when idle.
  const [phaseLabel, setPhaseLabel] = useState(null);
  const sessionId = useRef(initial?.sessionId || `pulse-${Date.now()}`);

  // Persist on every message change. Loading + phase are NOT persisted —
  // they're transient and only meaningful during an active request.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ messages, sessionId: sessionId.current })
      );
    } catch {
      /* quota or disabled — ignore */
    }
  }, [messages]);

  const send = useCallback(async (text, origin = "rail", context = null) => {
    const q = (text || "").trim();
    if (!q) return;
    setMessages((m) => [
      ...m,
      { role: "user", content: q, context: context || null, ts: Date.now() },
    ]);
    setLoading(true);
    setPhaseLabel("מתחיל…");

    try {
      let finalEvt = null;
      let errorEvt = null;
      await sendChatStream(sessionId.current, q, (evt) => {
        if (!evt || !evt.phase) return;
        if (evt.phase === "done") {
          finalEvt = evt;
        } else if (evt.phase === "error") {
          errorEvt = evt;
        } else if (evt.label) {
          setPhaseLabel(evt.label);
        }
      });

      if (errorEvt) {
        // Server-reported error (e.g. AI provider 502). Treat like an HTTP error.
        const err = new Error(errorEvt.detail || "AI error");
        err.response = { status: errorEvt.status || 500 };
        throw err;
      }
      if (!finalEvt) {
        throw new Error("Stream ended without a final reply");
      }

      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: finalEvt.reply,
          tools_used: finalEvt.tools_used || [],
          can_answer: finalEvt.can_answer !== false,
          transparency: finalEvt.transparency || null,
          rationale: finalEvt.rationale || null,
          ts: Date.now(),
          origin,
        },
      ]);
    } catch (err) {
      // Build a helpful, specific error message instead of swallowing everything.
      const isAbort = err?.name === "AbortError";
      const isNetwork = err?.message?.includes("Failed to fetch") || err?.message?.includes("NetworkError");
      const status = err?.response?.status;
      let errMsg;
      if (isAbort) {
        errMsg = "הבקשה בוטלה.";
      } else if (status === 401) {
        errMsg = "הסשן פג. רענן את הדף ותתחבר מחדש.";
      } else if (status >= 500) {
        errMsg = "שגיאה בשרת ה-AI. נסה שוב בעוד רגע — אם זה ממשיך, פתח דיווח באג.";
      } else if (isNetwork) {
        errMsg = "אין חיבור לשרת. בדוק את הרשת ונסה שוב.";
      } else {
        errMsg = "סליחה, משהו השתבש. נסה שוב או פתח את הצ׳אט המלא.";
      }
      // eslint-disable-next-line no-console
      console.error("[Pulse Chat] stream failed:", err);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: errMsg,
          isError: true,
          ts: Date.now(),
        },
      ]);
    } finally {
      setLoading(false);
      setPhaseLabel(null);
    }
  }, []);

  const reset = useCallback(() => {
    setMessages([]);
    sessionId.current = `pulse-${Date.now()}`;
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo(
    () => ({ messages, loading, phaseLabel, send, reset, sessionId: sessionId.current }),
    [messages, loading, phaseLabel, send, reset]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat must be used inside <ChatProvider>");
  return ctx;
}
